import { useEffect, useRef } from 'react';
import {
  useGetProjectQuery,
  usePatchProjectMutation,
} from 'app/services/projectBoardApi';
import type { TextEditField } from 'app/slices/projectBoardSlice';
import { extractContenteditableText } from '../projectBoardUtils';
import { autofocusAttr } from 'hooks/useAutofocusAttr';

const TITLES: Record<TextEditField, string> = {
  warranty: 'Project Warranty',
  submittal_cover_note: 'Submittal Cover Note',
};

/**
 * TextEditPopup (project/view/text_edit_popup.js → text_edit_popup.tmpl),
 * inventory §3.8 / parity 50–52: `.popup-wrapper.create-popup.active` with the
 * popup title, a contenteditable `div.textarea[maxlength=800]` pre-filled with
 * the field value (focused, caret at end), Save (autofocus) + Cancel.
 *
 * - Typing beyond maxlength is blocked by the global `.textarea` guard
 *   (useGlobalInputBehaviors).
 * - Paste is plain-text-only (execCommand insertText).
 * - Save: text extracted via the getContenteditableDivHTML port ('' → null);
 *   PATCHes only when it differs from the original value.
 * - ESC closes without saving (usePopupKeys clicks `button[name=cancel_btn]`);
 *   ENTER inside the contenteditable inserts a newline (contentEditable guard).
 *
 * Renders into the #text_edit_popup mount inside the General Info left column
 * (same DOM placement as Backbone's setElement target).
 */
interface TextEditPopupProps {
  projectId: number;
  name: TextEditField;
  onClose: () => void;
}

const TextEditPopup = ({ projectId, name, onClose }: TextEditPopupProps) => {
  const { data: project } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const [patchProject] = usePatchProjectMutation();
  const divRef = useRef<HTMLDivElement | null>(null);
  // Original value captured at open (Backbone passes options.value once).
  const originalRef = useRef<string | null>(
    (project?.[name] as string | null | undefined) ?? null
  );
  const changedRef = useRef<string | null>(originalRef.current);

  // Pre-fill + focus + caret-to-end on open (text_edit_popup.js render).
  useEffect(() => {
    const div = divRef.current;
    if (!div) return;
    div.textContent = originalRef.current ?? '';
    div.focus();
    if (document.createRange && window.getSelection) {
      const range = document.createRange();
      range.selectNodeContents(div);
      range.collapse(false); // place at end
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, []);

  const readText = () => {
    const div = divRef.current;
    if (!div) return changedRef.current;
    const text = extractContenteditableText(div, 800);
    return text || null; // '' → null
  };

  const onBlur = () => {
    changedRef.current = readText();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const onSave = () => {
    const changed = readText();
    if (changed !== originalRef.current) {
      patchProject({ id: projectId, body: { [name]: changed } });
    }
    onClose();
  };

  return (
    <div className="popup-wrapper create-popup active">
      <div className="popup-content col-xs-12">
        <div className="">
          <h2 className="popup-heading">{TITLES[name]}</h2>
        </div>
        <div className="col-xs-12">
          <div className="clearfix  popup-content-part">
            <div
              ref={divRef}
              maxlength="800"
              className="textarea"
              name={name}
              placeholder="800 characters maximum"
              contentEditable
              suppressContentEditableWarning
              onBlur={onBlur}
              onPaste={onPaste}
            />
          </div>
          <div className="popup-control-buttons col-xs-12">
            <div className="row">
              <button
                ref={autofocusAttr}
                className="save default-btn highlighted-btn"
                name="save_btn"
                onClick={onSave}
              >
                Save
              </button>
              <button className="not-highlighted-btn" name="cancel_btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextEditPopup;
